import { useCallback, useEffect, useRef, useState } from "react";

import { getLatestHomeAlbums } from "../services/homeAlbumsService";

const DEFAULT_LIMIT = 4;
const MAX_LIMIT = 50;

const normalizeLimit = (value) => {
  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed > 0
    ? Math.min(parsed, MAX_LIMIT)
    : DEFAULT_LIMIT;
};

const normalizeAlbums = (items, limit) => {
  const seen = new Set();

  return (Array.isArray(items) ? items : [])
    .filter((album) => {
      if (!album || typeof album !== "object") {
        return false;
      }

      const key = album.albumId ?? album.albumTag;

      if (key == null || key === "") {
        return false;
      }

      const identity = String(key);

      if (seen.has(identity)) {
        return false;
      }

      seen.add(identity);

      return true;
    })
    .slice(0, limit);
};

const normalizeResponse = (response) => {
  const data = response?.data ?? response;

  if (data?.success === false) {
    throw new Error(data.message || "Unable to load homepage albums.");
  }

  if (!data || !Array.isArray(data.albums)) {
    console.error("Invalid homepage albums payload:", data);
    throw new Error("Invalid homepage albums response.");
  }

  return {
    albums: data.albums,
    pagination: data.pagination || {},
    total: data.total ?? data.pagination?.total ?? data.albums.length,
  };
};

export const useHomeAlbums = ({
  limit = DEFAULT_LIMIT,
  autoLoad = true,
} = {}) => {
  const safeLimit = normalizeLimit(limit);

  const [albums, setAlbums] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(Boolean(autoLoad));
  const [error, setError] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  const requestIdRef = useRef(0);

  const refresh = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setLoading(false);
      return;
    }

    let active = true;

    const requestId = ++requestIdRef.current;
    const controller = new AbortController();

    const loadAlbums = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getLatestHomeAlbums({
          limit: safeLimit,
          offset: 0,
          signal: controller.signal,
        });

        if (
          !active ||
          controller.signal.aborted ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        const data = normalizeResponse(response);

        const nextAlbums = normalizeAlbums(data.albums, safeLimit);

        const responseTotal = Number(data.total);

        const validTotal =
          Number.isFinite(responseTotal) && responseTotal >= 0
            ? responseTotal
            : nextAlbums.length;

        setAlbums(nextAlbums);
        setTotal(validTotal);

        setHasMore(
          typeof data.pagination.hasMore === "boolean"
            ? data.pagination.hasMore
            : nextAlbums.length < validTotal,
        );
      } catch (requestError) {
        if (
          !active ||
          controller.signal.aborted ||
          requestId !== requestIdRef.current ||
          requestError?.code === "ERR_CANCELED" ||
          requestError?.name === "CanceledError" ||
          requestError?.name === "AbortError"
        ) {
          return;
        }

        console.error("Unable to load homepage albums:", requestError);

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load the latest albums.",
        );

        setAlbums([]);
        setTotal(0);
        setHasMore(false);
      } finally {
        if (active && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void loadAlbums();

    return () => {
      active = false;
      controller.abort();
    };
  }, [safeLimit, autoLoad, refreshKey]);

  return {
    albums,
    total,
    hasMore,
    hasAlbums: albums.length > 0,
    loading,
    error,
    refresh,
    getHomeAlbums: refresh,
  };
};

export default useHomeAlbums;
